// ─────────────────────────────────────────────
//  GeofenceGate — Board Manager device geofence.
//
//  A boardmanager account is a shared wall device that must physically be at
//  its facility. On login we ask the browser for the device position and
//  compare it against the facility's geofence:
//
//    • verifiably OUTSIDE the fence  → full-screen block (sign out only)
//    • geolocation permission DENIED → block with enable instructions
//    • geolocation UNAVAILABLE/error → amber warning banner, but ALLOW —
//      wall TVs / sticks often have no location sensor at all.
//    • NO GEOFENCE CONFIGURED        → amber banner, but ALLOW (see below).
//
//  WHERE THE FENCE COMES FROM (multi-tenant): the live
//  facilities/{id}.geofence map first, then this deployment's built-in
//  FACILITY_PINS, then nothing (domain/providerMap.js resolveGeofence). The
//  built-in pins are AdventHealth campuses; a different customer's hospital is
//  nowhere near them, so reading the doc is what makes the fence correct for
//  anyone but us.
//
//  UNCONFIGURED = ALLOW, LOUDLY. A fresh tenant has no coordinates on day one
//  and a board that refuses to load is worse than one that loads off-site. But
//  an invisible pass is indistinguishable from a verified device, so the
//  unconfigured case says so on screen. It is also checked BEFORE the denied-
//  permission block: refusing entry over a location we would not have compared
//  against anything would lock a new tenant out of their own board.
//
//  This is a deterrent, not hard security: browser geolocation is client-side
//  and spoofable. Real protection stays in Firestore rules (facility scoping).
//  Non-boardmanager users render children untouched — zero cost, no listener.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { logout } from "../firebase/auth";
import { useFacility } from "../hooks/useFacility";
import { checkGeofence, resolveGeofence } from "../domain/providerMap";

const S = {
  block: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "#0b1220",
    color: "#e2e8f0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    padding: 24,
    textAlign: "center",
    fontFamily: "inherit",
  },
  icon: { fontSize: 44 },
  title: { fontSize: 20, fontWeight: 700 },
  body: { maxWidth: 460, fontSize: 14, lineHeight: 1.5, color: "#94a3b8" },
  btn: {
    marginTop: 8,
    padding: "10px 22px",
    borderRadius: 8,
    border: "1px solid #334155",
    background: "#1e293b",
    color: "#e2e8f0",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  warn: {
    background: "#78350f",
    color: "#fde68a",
    padding: "6px 14px",
    fontSize: 13,
    textAlign: "center",
  },
};

export default function GeofenceGate({ children }) {
  const { user, isBoardManager, isPreviewing } = useAuth();
  // Device position only — idle | checking | fixed | denied | unavailable.
  // The VERDICT is derived at render time from this plus the live facility
  // doc, so a geofence that arrives after the position does re-evaluates
  // without re-prompting the browser for location.
  const [geo, setGeo] = useState({ status: "idle", coords: null });

  const facilityId = user?.activeFacilityId || null;
  // Only REAL boardmanager logins are fenced. An admin previewing the role
  // ("View as") is simulating the UI, not operating a wall device — fencing
  // the preview would lock the admin behind the block screen.
  const active = !!user && isBoardManager && !isPreviewing && !!facilityId;

  // The facility's own geofence. Passing null when the gate is inert keeps the
  // listener off for every other user — GeofenceGate wraps the whole app, so
  // an unconditional subscription here would be a snapshot per signed-in user
  // for a field only wall devices read. A failed/absent doc yields null, which
  // resolves to the built-in pin (this deployment) or to "unconfigured" —
  // never to a fabricated fence.
  const { facility } = useFacility(active ? facilityId : null);

  useEffect(() => {
    if (!active) {
      setGeo({ status: "idle", coords: null });
      return undefined;
    }
    if (!("geolocation" in navigator)) {
      setGeo({ status: "unavailable", coords: null });
      return undefined;
    }
    let dead = false;
    setGeo({ status: "checking", coords: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (dead) return;
        setGeo({
          status: "fixed",
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        });
      },
      (err) => {
        if (dead) return;
        // 1 = PERMISSION_DENIED → block; anything else (position unavailable,
        // timeout) → warn-but-allow, the device may simply lack a sensor.
        setGeo({
          status: err && err.code === 1 ? "denied" : "unavailable",
          coords: null,
        });
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
    );
    return () => {
      dead = true;
    };
  }, [active, facilityId]);

  if (!active) return children;

  const { pin, source } = resolveGeofence(facilityId, facility);
  const facilityName =
    facility?.name || pin?.label || user?.activeFacilityName || "this facility";

  // Nothing to compare against. Allow — but announce it, and do it BEFORE the
  // denied/unavailable branches so a missing fence can never become a lockout.
  if (source === "unconfigured") {
    return (
      <>
        <div style={S.warn}>
          ⚠ No geofence configured for {facilityName} — this Board Manager
          device is not location-restricted. Store the campus coordinates on the
          facility record to turn the fence on.
        </div>
        {children}
      </>
    );
  }

  const verdict =
    geo.status === "fixed"
      ? checkGeofence(facilityId, geo.coords.lat, geo.coords.lng, undefined, facility)
      : null;
  if (verdict?.ok) return children;
  const distance = verdict?.distance ?? null;
  const radiusM = verdict?.radius ?? pin.radiusMeters;

  if (geo.status === "idle" || geo.status === "checking") {
    return (
      <div style={S.block}>
        <div style={S.icon}>📍</div>
        <div style={S.title}>Verifying device location…</div>
        <div style={S.body}>
          Board Manager devices only work on-site at {facilityName}.
        </div>
      </div>
    );
  }

  if (verdict) {
    // Verifiably outside, or a position we could not parse — both fail closed
    // now that we KNOW this facility has a fence to fail against.
    const km = Number.isFinite(distance) ? (distance / 1000).toFixed(1) : null;
    return (
      <div style={S.block}>
        <div style={S.icon}>🚫</div>
        <div style={S.title}>Outside facility area</div>
        <div style={S.body}>
          This Board Manager account can only be used at {facilityName}
          {km ? ` — this device is about ${km} km away` : ""} (allowed radius{" "}
          {Math.round(radiusM / 100) / 10} km). Move the device
          on-site and sign in again.
        </div>
        <button style={S.btn} onClick={logout}>
          Sign out
        </button>
      </div>
    );
  }

  if (geo.status === "denied") {
    return (
      <div style={S.block}>
        <div style={S.icon}>📍</div>
        <div style={S.title}>Location permission required</div>
        <div style={S.body}>
          Board Manager accounts are geofenced to {facilityName}. Enable
          location access for this site in the browser settings (usually the
          lock / tune icon next to the address bar → Site settings → Location →
          Allow), then reload.
        </div>
        <button style={S.btn} onClick={() => window.location.reload()}>
          Reload
        </button>
        <button style={S.btn} onClick={logout}>
          Sign out
        </button>
      </div>
    );
  }

  // unavailable — warn but allow (many TV devices have no location sensor).
  return (
    <>
      <div style={S.warn}>
        ⚠ Device location unavailable — geofence for {facilityName} could not
        be verified.
      </div>
      {children}
    </>
  );
}
